import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { GetTailBitingsResponse, UpsertTailBitingResponse } from './tail-biting-service.model';
import { TailBiting } from '../model/tail-biting.model';

@Injectable({
  providedIn: 'root',
})
export class TailBitingService {
  protected baseUrl = `${this.env.API_BASE_URL}/service/pig-registration/secure`;

  serviceWorkerRegistration: ServiceWorkerRegistration;

  constructor(
    protected http: HttpClient,
    @Inject(EnvironmentInjector) private readonly env: { API_BASE_URL: string },
    private readonly localeService: LocaleService,
  ) {}

  getTailBitingById(id: number): Observable<TailBiting | HttpErrorResponse> {
    return this.http.get<TailBiting>(`${this.baseUrl}/tailbiting/${id}`).pipe(
      map((response) => response),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<TailBiting>{});
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  getTailBitings(): Observable<TailBiting[] | HttpErrorResponse> {
    return this.http.get<GetTailBitingsResponse>(`${this.baseUrl}/tailbiting`).pipe(
      map((response) => response.tailBitings),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<TailBiting[]>[]);
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  upsertTailBiting(
    tailBiting: TailBiting,
  ): Observable<UpsertTailBitingResponse | HttpErrorResponse> {
    return this.http
      .post<UpsertTailBitingResponse>(`${this.baseUrl}/tailbiting`, tailBiting, {
        headers: {
          OfflineIdentifier: 'UpsertTailBiting',
          OfflineResponse: '{"id": 0, "offline": true}',
        },
      })
      .pipe(
        map((response) => response),
        catchError(([error, response]: [HttpErrorResponse, UpsertTailBitingResponse]) => {
          if (error.status === 304) {
            return of({
              isOffline: true,
              id: response.id,
            } as UpsertTailBitingResponse);
          } else if (error.status === 409) {
            return throwError({
              message: this.localeService.translate('create-tailbiting.error-code-already-in-use'),
            });
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  deleteTailBiting(tailBitingId: number) {
    return this.http.delete(`${this.baseUrl}/tailbiting/${tailBitingId}`);
  }
}
